<script setup>
import { Head, Link } from '@inertiajs/vue3';
import FrontLayout from '@/Layouts/FrontLayout.vue';

const props = defineProps({
    setting: Object,
    status: {
        type: Number,
        default: 404
    }
});

const title = `${props.status} - Halaman Tidak Ditemukan | Gotransport`;
const description = 'Halaman yang Anda cari tidak ditemukan. Silakan kembali ke beranda Gotransport atau hubungi kami untuk bantuan mencari informasi rental mobil.';

const waNumber = props.setting?.setting_no_wa || '6281234567890';
const waMessage = encodeURIComponent(`Hai ${props.setting?.setting_title || 'Gotransport'}, saya butuh bantuan menemukan halaman yang saya cari.`);
const waUrl = `https://wa.me/${waNumber}?text=${waMessage}`;
</script>

<template>
    <FrontLayout :setting="props.setting" :title="title" :metaDescription="description">
        <Head>
            <title>{{ title }}</title>
            <meta name="robots" content="noindex, follow" />
            <meta name="description" :content="description" />
        </Head>

        <!-- 404 Error Section -->
        <section class="min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 py-16 px-4">
            <div class="container mx-auto max-w-4xl">
                <div class="text-center">
                    <!-- Error Code -->
                    <div class="mb-6">
                        <h1 class="text-[8rem] md:text-[10rem] font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500 leading-none drop-shadow-sm">
                            {{ status }}
                        </h1>
                    </div>
                    
                    <!-- Error Message -->
                    <div class="mb-8">
                        <h2 class="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
                            Oops! Halaman Tidak Ditemukan
                        </h2>
                        <p class="text-gray-600 text-lg max-w-xl mx-auto leading-relaxed">
                            Maaf, halaman yang Anda cari tidak dapat ditemukan. 
                            Mungkin halaman telah dipindahkan atau URL yang dimasukkan salah.
                        </p>
                    </div>
                    
                    <!-- Action Buttons -->
                    <div class="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
                        <Link 
                            href="/" 
                            class="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                        >
                            <i class="fa fa-home"></i>
                            Kembali ke Beranda
                        </Link>
                        <Link 
                            href="/daftar-armada" 
                            class="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-800 font-bold px-8 py-4 rounded-full border-2 border-gray-200 hover:border-orange-300 shadow-md transition-all duration-300"
                        >
                            <i class="fa fa-car"></i>
                            Lihat Armada
                        </Link>
                    </div>
                    
                    <!-- Secondary Links -->
                    <div class="flex flex-wrap items-center justify-center gap-4 mb-10">
                        <Link 
                            href="/contact" 
                            class="inline-flex items-center gap-2 text-gray-600 hover:text-orange-600 font-medium transition-colors"
                        >
                            <i class="fa fa-envelope"></i>
                            Hubungi Kami
                        </Link>
                        <span class="text-gray-300">|</span>
                        <Link 
                            href="/berita-dan-artikel" 
                            class="inline-flex items-center gap-2 text-gray-600 hover:text-orange-600 font-medium transition-colors"
                        >
                            <i class="fa fa-newspaper-o"></i>
                            Berita & Artikel
                        </Link>
                        <span class="text-gray-300">|</span>
                        <a 
                            :href="waUrl" 
                            target="_blank" 
                            rel="nofollow noopener"
                            class="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-medium transition-colors"
                        >
                            <i class="fa fa-whatsapp"></i>
                            WhatsApp
                        </a>
                    </div>
                    
                    <!-- Helpful Links -->
                    <div class="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 max-w-2xl mx-auto">
                        <h3 class="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">
                            Halaman Populer
                        </h3>
                        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <Link 
                                href="/daftar-armada/lepas-kunci" 
                                class="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-orange-50 border border-transparent hover:border-orange-200 transition-all group"
                            >
                                <span class="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 group-hover:bg-orange-200 transition-colors">
                                    <i class="fa fa-key"></i>
                                </span>
                                <span class="font-medium text-gray-700 group-hover:text-orange-600 transition-colors">Lepas Kunci</span>
                            </Link>
                            <Link 
                                href="/daftar-armada/dengan-sopir" 
                                class="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-blue-50 border border-transparent hover:border-blue-200 transition-all group"
                            >
                                <span class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 group-hover:bg-blue-200 transition-colors">
                                    <i class="fa fa-user"></i>
                                </span>
                                <span class="font-medium text-gray-700 group-hover:text-blue-600 transition-colors">Dengan Sopir</span>
                            </Link>
                            <Link 
                                href="/daftar-armada/berdasarkan-jenis" 
                                class="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-purple-50 border border-transparent hover:border-purple-200 transition-all group"
                            >
                                <span class="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 group-hover:bg-purple-200 transition-colors">
                                    <i class="fa fa-tags"></i>
                                </span>
                                <span class="font-medium text-gray-700 group-hover:text-purple-600 transition-colors">Jenis Kendaraan</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </FrontLayout>
</template>
